import { useState } from 'react'
import { Disc3, Download } from 'lucide-react'
import { installIso as api } from '@/api/endpoints'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Ago } from '@/components/Ago'
import { Err } from '@/components/Err'
import { ExpertLine } from '@/components/Expert'
import { ToneBadge } from '@/components/ToneBadge'
import { Empty, PageHead, Section, SectionCrumb } from '@/components/layout/Page'
import { ISO_PROGRESS_WORD, ISO_WORD, isoNeedsRebuild, isoStatus } from '@/domain/iso'
import { useMinute } from '@/hooks/useNow'
import { usePoll } from '@/hooks/usePoll'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { bytes, exact } from '@/ui/format'

const POLL_MS = 5000

function date(iso: string): string {
  const value = new Date(iso)
  if (Number.isNaN(value.getTime())) return '—'
  return new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium', timeStyle: 'short' }).format(value)
}

export default function InstallIso() {
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [actionError, setActionError] = useState<Error | null>(null)
  const current = usePoll(() => api.current(), useFallback(POLL_MS))
  useRefetchOn((c) => c.kind === 'iso', current.refresh)
  const iso = current.data
  // 없음(404)은 상태가 아니라 빈 자리다. 실패와 만료는 다시 만들어야 하는 상태다.
  // 만료는 등록 기한과 지금의 비교라 시계로 다시 본다 — 같은 응답은 화면을 다시 그리지 않는다.
  const now = useMinute()
  const status = iso ? isoStatus(iso, now) : null
  const word = status ? ISO_WORD[status] : null
  const rebuild = status !== null && isoNeedsRebuild(status)
  const verb = rebuild ? 'ISO 재생성' : 'ISO 생성'
  const progress = iso ? Math.min(100, Math.max(0, iso.progress.percent)) : 0
  const progressLabel = iso ? ISO_PROGRESS_WORD[iso.progress.stage] : ''

  const create = async () => {
    setCreating(true)
    setActionError(null)
    try {
      await api.create()
      setOpen(false)
      current.refresh()
    } catch (error) {
      setActionError(error instanceof Error ? error : new Error(String(error)))
    } finally {
      setCreating(false)
    }
  }

  // 첨부 응답은 페이지를 떠나지 않는다. 링크를 건네고 나면 버튼은 다시 받기여야 하고, 또 누를 수 있어야 한다.
  const download = async () => {
    setDownloading(true)
    setActionError(null)
    try {
      await api.download()
    } catch (error) {
      setActionError(error instanceof Error ? error : new Error(String(error)))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <SectionCrumb />
      <PageHead
        icon={<Disc3 className="size-5" />}
        title="설치 ISO"
        facts={word ? word.label : current.loading ? '불러오는 중' : 'ISO 없음'}
        right={
          !iso || rebuild ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button>{verb}</Button>
              </DialogTrigger>
              <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                  <DialogTitle>{rebuild ? '설치 ISO 재생성' : '설치 ISO 생성'}</DialogTitle>
                </DialogHeader>
                <Err error={actionError} />
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" disabled={creating}>취소</Button>
                  </DialogClose>
                  <Button onClick={() => void create()} disabled={creating}>
                    {creating ? '요청 중' : '생성'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      <Section title="ISO">
        <Err error={current.error ?? actionError} />
        {!iso && !current.loading ? (
          <Empty>ISO 없음</Empty>
        ) : iso && word ? (
          <div className="flex flex-wrap items-center gap-4 rounded-lg border bg-card px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="font-medium">server-manager.iso</div>
              <div className="mt-1 text-[12px] text-muted-foreground">등록 기한 {date(iso.expires_at)}</div>
              {/* 행이 아직 없는 장비의 "USB 가 닿기는 했나" 는 여기서만 답한다(#325). */}
              {status === 'ready' || status === 'outdated' ? (
                <div className="mt-1 text-[12px] text-muted-foreground">
                  {iso.last_contact_at ? <>마지막 장비 접속 <Ago at={iso.last_contact_at} /></> : '아직 이 ISO 로 들어온 장비가 없습니다.'}
                </div>
              ) : null}
              <ExpertLine
                items={[
                  `생성 ${exact(iso.created_at)}`,
                  iso.finished_at ? `완료 ${exact(iso.finished_at)}` : null,
                  iso.bytes ? bytes(iso.bytes) : null,
                  iso.sha256 ? <span key="sha" className="font-mono">sha256 {iso.sha256}</span> : null,
                ]}
              />
              {status === 'ready' ? (
                <div className="mt-1 text-[12px] text-muted-foreground">부팅 전에 BIOS 에서 IOMMU 를 켜고 BMC 의 USB LAN 을 끄세요.</div>
              ) : null}
              {status === 'outdated' ? (
                <div className="mt-1 text-[12px] text-muted-foreground">설치기가 바뀌었습니다. ISO 를 다시 만들어 그것으로 설치하세요.</div>
              ) : null}
              {status === 'pending' || status === 'building' ? (
                <div className="mt-2 w-40">
                  <div className="mb-1 flex justify-between text-[11px] text-muted-foreground">
                    <span>{progressLabel}</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} aria-label={`${progressLabel} ${progress}%`} className="h-1 bg-muted" />
                </div>
              ) : null}
            </div>
            <ToneBadge tone={word.tone}>{word.label}</ToneBadge>
            {status === 'ready' ? (
              <Button variant="outline" size="sm" disabled={downloading} onClick={() => void download()}>
                <Download />
                {downloading ? '준비 중' : '받기'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </Section>
    </>
  )
}
